import {
  CoogoExecutionError,
  validateCoogoExecutionTransition,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
  type CoogoExecutionCheckpointV1,
  type CoogoExecutionLease,
  type CoogoExecutionTransition,
  type CoogoExecutionTransitionReceipt,
  type CoogoExecutionEvent,
  type CoogoReadableExecutionEvent,
  type CoogoReadableOrchestrationPlan,
  type CoogoOrchestrationPlan,
} from "@cogo/webmcp-runtime";
import {
  candidateLifecycleEventFromJournalData,
  deriveCandidateLifecycleProjection,
  type CandidateLifecycleEvent,
  type CandidateLifecycleProjection,
} from "@cogo/schema/productization-spine";
import {
  getCogoPlatformBase,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";
import { useStudioStore } from "@/store/studio-store";
import { isStudioOrchestrationValueSafe } from "./studio-orchestration-sensitive-data";

const REQUEST_TIMEOUT_MS = 15_000;
export const isStudioOrchestrationJournalSafe = isStudioOrchestrationValueSafe;

export interface StudioOrchestrationRunReadback {
  run: {
    planId: string;
    runId: string;
    projectId: string;
    status:
      | "accepted"
      | "suspended"
      | "running"
      | "completed"
      | "failed"
      | "cancelled";
    lastSequence: number;
    createdAt: string;
    updatedAt: string;
    checkpointRevision?: number;
  };
  plan: CoogoOrchestrationPlan;
  events: CoogoExecutionEvent[];
  checkpoint?: CoogoExecutionCheckpointV1 | null;
}

export interface StudioReadableOrchestrationRunReadback extends Omit<
  StudioOrchestrationRunReadback,
  "plan" | "events"
> {
  plan: CoogoReadableOrchestrationPlan;
  events: CoogoReadableExecutionEvent[];
}

export interface StudioOrchestrationRunReference {
  runId: string;
  projectId: string;
  planId: string;
  createdAt: string;
  expiresAt: string;
}

export type StudioOrchestrationJournalMode = "durable" | "session-only";

export class StudioOrchestrationJournalRequestError extends CoogoExecutionError {
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message, code);
    this.name = "StudioOrchestrationJournalRequestError";
    this.status = status;
  }
}

export interface StudioOrchestrationResumeGrantReceipt {
  grantToken: string;
  expiresAt: string;
  checkpointRevision: number;
}

export interface StudioExecutionLeaseReceipt extends CoogoExecutionLease {
  checkpointRevision: number;
}

function exactRecord(
  value: unknown,
  keys: readonly string[],
): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
    ? record
    : null;
}

function receiptMismatch(message: string): CoogoExecutionError {
  return new CoogoExecutionError(message, "lease_receipt_mismatch");
}

function accessToken(): string | null {
  return useStudioStore.getState().session?.access_token?.trim() || null;
}

function authenticatedJournalPrincipal(): {
  token: string;
  userRef: string;
} | null {
  const session = useStudioStore.getState().session;
  const token = session?.access_token?.trim();
  const userId = session?.user?.id?.trim();
  return token && userId ? { token, userRef: `user:${userId}` } : null;
}

function assertStudioOrchestrationJournalEvent(
  event: CoogoExecutionEvent,
): void {
  const validation = validateCoogoExecutionEvent(event);
  if (!validation.valid) {
    throw new Error(
      `Invalid orchestration journal event: ${validation.errors.join("; ")}`,
    );
  }
  if (!isStudioOrchestrationJournalSafe(event)) {
    throw new Error("Sensitive orchestration events must remain session-only.");
  }
}

async function persistStudioOrchestrationJournalEvent(
  event: CoogoExecutionEvent,
  token: string,
): Promise<"durable"> {
  // The Platform append endpoint performs candidate lifecycle admission through
  // COGO_PRODUCTIZATION_AUTHORITY_URL before it writes the durable journal.
  // Keep that server-mediated boundary singular; the browser must not pre-admit
  // the same sequence through the Studio authority endpoint.
  await requestJson(
    `/api/orchestration/runs/${encodeURIComponent(event.runId)}/events`,
    {
      method: "POST",
      body: JSON.stringify({ projectId: event.projectId, event }),
    },
    token,
  );
  return "durable";
}

function assertCandidateLifecycleNodeBinding(input: {
  candidateEvent: CandidateLifecycleEvent;
  node: CoogoOrchestrationPlan["nodes"][number];
  authenticatedUserRef: string;
}): void {
  const { candidateEvent, node, authenticatedUserRef } = input;
  if (candidateEvent.transition === "verify") {
    if (
      !["gate", "readback"].includes(node.kind) ||
      !["server", "runtime"].includes(node.policy.authority)
    ) {
      throw new Error(
        "Candidate verification requires a server/runtime gate or readback node.",
      );
    }
  }
  if (["approve", "retire"].includes(candidateEvent.transition)) {
    const action =
      candidateEvent.transition === "approve" ? "approval" : "retirement";
    if (
      node.kind !== "human" ||
      node.policy.authority !== "human" ||
      !["user", "typed"].includes(node.policy.confirmation)
    ) {
      throw new Error(
        `Candidate ${action} requires a confirmed human orchestration node.`,
      );
    }
    if (candidateEvent.actor.actorRef !== authenticatedUserRef) {
      throw new Error(
        `Candidate ${action} actor does not match the authenticated Studio user.`,
      );
    }
  }
  if (
    candidateEvent.transition === "instantiate" &&
    (node.kind !== "readback" ||
      !["server", "runtime"].includes(node.policy.authority))
  ) {
    throw new Error(
      "Customer instantiation requires a server/runtime readback node.",
    );
  }
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
  token: string,
): Promise<T> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    REQUEST_TIMEOUT_MS,
  );
  try {
    const response = await fetch(`${getCogoPlatformBase()}${path}`, {
      ...init,
      headers: withCogoDevProxyHeaderRecord({
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(init.body ? { "Content-Type": "application/json" } : {}),
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const errorBody = (await response
        .clone()
        .json()
        .catch(() => null)) as { error?: unknown; code?: unknown } | null;
      throw new StudioOrchestrationJournalRequestError(
        typeof errorBody?.error === "string"
          ? errorBody.error
          : `Orchestration journal request failed (${response.status}).`,
        typeof errorBody?.code === "string"
          ? errorBody.code
          : "orchestration_journal_request_failed",
        response.status,
      );
    }
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("Orchestration journal request timed out.");
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

function durableAccessToken(): string {
  const token = accessToken();
  if (!token) {
    throw new CoogoExecutionError(
      "Authenticated durable orchestration authority is required.",
      "durable_orchestration_auth_required",
    );
  }
  return token;
}

function assertSafeAtomicTransition(
  transition: CoogoExecutionTransition,
): void {
  const validation = validateCoogoExecutionTransition(transition);
  if (!validation.valid) {
    throw new CoogoExecutionError(
      `Invalid execution transition: ${validation.errors.join("; ")}`,
      "invalid_transition",
    );
  }
  // leaseId/fencingToken are required transport authority and are validated by
  // the closed transition shape above. They must not enter the durable event or
  // checkpoint, whose payloads are scanned independently here.
  if (
    !isStudioOrchestrationJournalSafe(transition.event) ||
    !isStudioOrchestrationJournalSafe(transition.nextCheckpoint)
  ) {
    throw new CoogoExecutionError(
      "Sensitive execution transition material is forbidden.",
      "unsafe_transition_material",
    );
  }
}

function parseTransitionReceipt(
  value: unknown,
  transition: CoogoExecutionTransition,
): CoogoExecutionTransitionReceipt {
  const receipt = exactRecord(value, [
    "checkpointRevision",
    "eventId",
    "sequence",
    "replayed",
  ]);
  if (
    !receipt ||
    receipt.eventId !== transition.event.eventId ||
    receipt.sequence !== transition.event.sequence ||
    !Number.isInteger(receipt.checkpointRevision) ||
    receipt.checkpointRevision !== transition.expectedCheckpointRevision + 1 ||
    typeof receipt.replayed !== "boolean"
  ) {
    throw new CoogoExecutionError(
      "Platform acknowledged a different execution transition.",
      "journal_receipt_mismatch",
    );
  }
  return {
    checkpointRevision: receipt.checkpointRevision as number,
    eventId: receipt.eventId as string,
    sequence: receipt.sequence as number,
    replayed: receipt.replayed,
  };
}

/** The only Studio write path for v2 execution events and checkpoints. */
export async function commitStudioOrchestrationTransition(
  transition: CoogoExecutionTransition,
): Promise<CoogoExecutionTransitionReceipt> {
  assertSafeAtomicTransition(transition);
  const token = durableAccessToken();
  const path = `/api/orchestration/runs/${encodeURIComponent(transition.event.runId)}/transitions`;
  const body = JSON.stringify({
    projectId: transition.event.projectId,
    ...transition,
  });
  let value: unknown;
  let replayRequired = false;
  try {
    value = await requestJson<unknown>(path, { method: "POST", body }, token);
  } catch (error) {
    if (error instanceof StudioOrchestrationJournalRequestError) throw error;
    replayRequired = true;
    value = await requestJson<unknown>(path, { method: "POST", body }, token);
  }
  const response = parseTransitionReceipt(value, transition);
  if (replayRequired && !response.replayed) {
    throw new CoogoExecutionError(
      "A lost transition response must be acknowledged as an exact replay.",
      "journal_replay_receipt_required",
    );
  }
  return response;
}

export async function requestStudioOrchestrationResumeGrant(input: {
  projectId: string;
  runId: string;
  gateNodeId: string;
  confirmationId: string;
  target: {
    kind: "studio-page";
    projectId: string;
    pageId: string;
  };
  expectedCheckpointRevision: number;
}): Promise<StudioOrchestrationResumeGrantReceipt> {
  const { runId, ...body } = input;
  const value = await requestJson<unknown>(
    `/api/orchestration/runs/${encodeURIComponent(runId)}/resume-grants`,
    { method: "POST", body: JSON.stringify(body) },
    durableAccessToken(),
  );
  const receipt = exactRecord(value, [
    "grantToken",
    "expiresAt",
    "checkpointRevision",
  ]);
  if (
    !receipt ||
    typeof receipt.grantToken !== "string" ||
    !receipt.grantToken ||
    typeof receipt.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(receipt.expiresAt)) ||
    !Number.isInteger(receipt.checkpointRevision) ||
    (receipt.checkpointRevision as number) < 0
  ) {
    throw receiptMismatch("Platform returned an invalid resume grant receipt.");
  }
  return receipt as unknown as StudioOrchestrationResumeGrantReceipt;
}

export async function requestStudioExecutionLease(input: {
  projectId: string;
  runId: string;
  grantToken: string;
  clientInstanceId: string;
}): Promise<StudioExecutionLeaseReceipt> {
  const { runId, ...body } = input;
  const value = await requestJson<unknown>(
    `/api/orchestration/runs/${encodeURIComponent(runId)}/lease-acquire`,
    { method: "POST", body: JSON.stringify(body) },
    durableAccessToken(),
  );
  const receipt = exactRecord(value, [
    "leaseId",
    "fencingToken",
    "expiresAt",
    "checkpointRevision",
  ]);
  if (
    !receipt ||
    typeof receipt.leaseId !== "string" ||
    !receipt.leaseId ||
    !Number.isInteger(receipt.fencingToken) ||
    (receipt.fencingToken as number) <= 0 ||
    typeof receipt.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(receipt.expiresAt)) ||
    !Number.isInteger(receipt.checkpointRevision) ||
    (receipt.checkpointRevision as number) < 0
  ) {
    throw receiptMismatch(
      "Platform returned an invalid lease acquire receipt.",
    );
  }
  return receipt as unknown as StudioExecutionLeaseReceipt;
}

type StudioLeaseAuthorityInput = {
  projectId: string;
  runId: string;
  leaseId: string;
  fencingToken: number;
  clientInstanceId: string;
};

export async function requestStudioExecutionLeaseRenewal(
  input: StudioLeaseAuthorityInput,
): Promise<CoogoExecutionLease> {
  const { runId, ...body } = input;
  const value = await requestJson<unknown>(
    `/api/orchestration/runs/${encodeURIComponent(runId)}/lease-renew`,
    { method: "POST", body: JSON.stringify(body) },
    durableAccessToken(),
  );
  const receipt = exactRecord(value, ["leaseId", "fencingToken", "expiresAt"]);
  if (
    !receipt ||
    receipt.leaseId !== input.leaseId ||
    receipt.fencingToken !== input.fencingToken ||
    typeof receipt.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(receipt.expiresAt))
  ) {
    throw receiptMismatch(
      "Platform returned an invalid lease renewal receipt.",
    );
  }
  return receipt as unknown as CoogoExecutionLease;
}

export async function requestStudioExecutionLeaseRelease(
  input: StudioLeaseAuthorityInput,
): Promise<{ released: true }> {
  const { runId, ...body } = input;
  const value = await requestJson<unknown>(
    `/api/orchestration/runs/${encodeURIComponent(runId)}/lease-release`,
    { method: "POST", body: JSON.stringify(body) },
    durableAccessToken(),
  );
  const receipt = exactRecord(value, ["released"]);
  if (!receipt || receipt.released !== true) {
    throw receiptMismatch(
      "Platform returned an invalid lease release receipt.",
    );
  }
  return { released: true };
}

export async function createStudioOrchestrationJournal(
  plan: CoogoReadableOrchestrationPlan,
): Promise<StudioOrchestrationJournalMode> {
  const validation = validateCoogoOrchestrationPlan(plan);
  if (!validation.valid) {
    throw new Error(
      `Invalid orchestration journal plan: ${validation.errors.join("; ")}`,
    );
  }
  if (!isStudioOrchestrationJournalSafe(plan)) {
    throw new Error("Sensitive orchestration plans must remain session-only.");
  }
  const token = accessToken();
  if (!token) return "session-only";
  await requestJson(
    "/api/orchestration/runs",
    {
      method: "POST",
      body: JSON.stringify({ plan }),
    },
    token,
  );
  return "durable";
}

export async function appendStudioOrchestrationJournalEvent(
  event: CoogoExecutionEvent,
): Promise<StudioOrchestrationJournalMode> {
  assertStudioOrchestrationJournalEvent(event);
  if (candidateLifecycleEventFromJournalData(event.data)) {
    throw new Error(
      "Candidate lifecycle events must use appendStudioCandidateLifecycleJournalEvent.",
    );
  }
  const token = accessToken();
  if (!token) return "session-only";
  return persistStudioOrchestrationJournalEvent(event, token);
}

/**
 * Persists a generation/publishing-owned lifecycle transition through the
 * existing append-only orchestration journal. Studio validates and transports
 * the event to Platform, whose append path performs server-side authority
 * admission before persistence. The browser never mints an admission receipt
 * or creates a second mutable candidate state store.
 */
export async function appendStudioCandidateLifecycleJournalEvent(
  event: CoogoExecutionEvent,
): Promise<StudioOrchestrationJournalMode> {
  assertStudioOrchestrationJournalEvent(event);
  if (event.type !== "node.completed") {
    throw new Error(
      "Candidate lifecycle transitions must be emitted by a completed orchestration node.",
    );
  }
  const candidateEvent = candidateLifecycleEventFromJournalData(event.data);
  if (!candidateEvent) {
    throw new Error("Candidate lifecycle journal payload is required.");
  }
  if (
    candidateEvent.projectId !== event.projectId ||
    candidateEvent.journalRunId !== event.runId
  ) {
    throw new Error(
      "Candidate lifecycle journal project or run scope does not match.",
    );
  }
  if (
    candidateEvent.transitionId !== event.eventId ||
    candidateEvent.occurredAt !== event.occurredAt
  ) {
    throw new Error(
      "Candidate lifecycle transition identity does not match its journal envelope.",
    );
  }
  const principal = authenticatedJournalPrincipal();
  if (!principal) {
    throw new Error(
      "Candidate lifecycle transitions require an authenticated durable journal.",
    );
  }
  const readback = await readStudioOrchestrationJournal({
    projectId: event.projectId,
    runId: event.runId,
  });
  if (!readback) {
    throw new Error(
      "Candidate lifecycle durable journal readback is required.",
    );
  }
  if (event.sequence !== readback.run.lastSequence + 1) {
    throw new Error("Candidate lifecycle journal sequence is stale.");
  }
  const node = readback.plan.nodes.find(
    (candidate) => candidate.id === event.nodeId,
  );
  if (readback.run.planId !== event.planId || !node) {
    throw new Error("Candidate lifecycle journal plan binding is invalid.");
  }
  assertCandidateLifecycleNodeBinding({
    candidateEvent,
    node,
    authenticatedUserRef: principal.userRef,
  });
  const current = deriveStudioCandidateLifecycleProjections(
    readback.events,
  ).find(
    (projection) =>
      projection.skuId === candidateEvent.skuId &&
      projection.candidateId === candidateEvent.candidateId &&
      projection.journalRunId === candidateEvent.journalRunId,
  );
  if (candidateEvent.aggregateVersion === 1) {
    if (current) {
      throw new Error("Candidate lifecycle aggregate already exists.");
    }
  } else if (
    !current ||
    candidateEvent.aggregateVersion !== current.aggregateVersion + 1 ||
    candidateEvent.previousTransitionId !== current.lastTransitionId ||
    candidateEvent.fromState !== current.state
  ) {
    throw new Error("Candidate lifecycle aggregate transition is stale.");
  }
  return persistStudioOrchestrationJournalEvent(event, principal.token);
}

export function deriveStudioCandidateLifecycleProjections(
  events: CoogoExecutionEvent[],
): CandidateLifecycleProjection[] {
  const grouped = new Map<string, CandidateLifecycleEvent[]>();
  for (const event of events) {
    const candidateEvent = candidateLifecycleEventFromJournalData(event.data);
    if (!candidateEvent) continue;
    if (event.type !== "node.completed" || !event.nodeId?.trim()) {
      throw new Error(
        "Candidate lifecycle transitions must be emitted by a completed orchestration node.",
      );
    }
    if (
      candidateEvent.projectId !== event.projectId ||
      candidateEvent.journalRunId !== event.runId
    ) {
      throw new Error(
        "Candidate lifecycle journal project or run scope does not match.",
      );
    }
    if (
      candidateEvent.transitionId !== event.eventId ||
      candidateEvent.occurredAt !== event.occurredAt
    ) {
      throw new Error(
        "Candidate lifecycle transition identity does not match its journal envelope.",
      );
    }
    const key = [
      candidateEvent.projectId,
      candidateEvent.journalRunId,
      candidateEvent.skuId,
      candidateEvent.candidateId,
    ].join("\0");
    const group = grouped.get(key) ?? [];
    group.push(candidateEvent);
    grouped.set(key, group);
  }
  return [...grouped.values()].map(deriveCandidateLifecycleProjection);
}

export async function readStudioOrchestrationJournal(input: {
  projectId: string;
  runId: string;
}): Promise<StudioOrchestrationRunReadback | null> {
  return (await readStudioReadableOrchestrationJournal(
    input,
  )) as StudioOrchestrationRunReadback | null;
}

/** Dual-reader used by the v2 executor adapter; historical v1 callers stay typed v1. */
export async function readStudioReadableOrchestrationJournal(input: {
  projectId: string;
  runId: string;
}): Promise<StudioReadableOrchestrationRunReadback | null> {
  const token = accessToken();
  if (!token) return null;
  return requestJson(
    `/api/orchestration/runs/${encodeURIComponent(input.runId)}?projectId=${encodeURIComponent(input.projectId)}`,
    { method: "GET" },
    token,
  );
}

export async function listStudioOrchestrationJournals(
  projectId: string,
): Promise<StudioOrchestrationRunReference[] | null> {
  const token = accessToken();
  if (!token) return null;
  const response = await requestJson<{
    runs?: StudioOrchestrationRunReference[];
  }>(
    `/api/orchestration/runs?projectId=${encodeURIComponent(projectId)}`,
    { method: "GET" },
    token,
  );
  return Array.isArray(response.runs) ? response.runs : [];
}

export async function cancelStudioOrchestrationJournal(input: {
  projectId: string;
  runId: string;
  reason?:
    | "user-requested"
    | "studio-user-cancelled-confirmation"
    | "journal-ack-failure";
}): Promise<StudioOrchestrationJournalMode> {
  const token = accessToken();
  if (!token) return "session-only";
  await requestJson(
    `/api/orchestration/runs/${encodeURIComponent(input.runId)}/cancel`,
    {
      method: "POST",
      body: JSON.stringify({
        projectId: input.projectId,
        reason: input.reason ?? "user-requested",
      }),
    },
    token,
  );
  return "durable";
}
